import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 300;
const MARGIN = { top: 20, right: 90, bottom: 50, left: 55 };

const SERIES_KEYS = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit'];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function PriceIndexComparison({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hiddenSeries, setHiddenSeries] = useState(new Set());
  const [resizeCount, setResizeCount] = useState(0);

  const toggleSeries = (key) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0 || !data.series) return;

    const rafId = requestAnimationFrame(() => {
      const svg = d3.select(svgRef.current);
      svg.selectAll('*').remove();

      const containerWidth = containerRef.current
        ? containerRef.current.clientWidth
        : 700;
      const width = Math.max(600, containerWidth);
      const innerW = width - MARGIN.left - MARGIN.right;
      const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

      svg
        .attr('viewBox', `0 0 ${width} ${CHART_HEIGHT}`)
        .attr('width', '100%')
        .attr('height', CHART_HEIGHT)
        .style('overflow', 'visible');

      const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

      const n = data.dates.length;
      const parsedDates = data.dates.map((d) => new Date(d));

      // X scale
      const x = d3.scaleTime()
        .domain(d3.extent(parsedDates))
        .range([0, innerW]);

      // Gather all visible values for Y domain
      const allValues = [];
      const activeKeys = SERIES_KEYS.filter((key) => !hiddenSeries.has(key) && data.series[key]);
      activeKeys.forEach((key) => {
        data.series[key].forEach((v) => {
          if (v != null) allValues.push(v);
        });
      });

      // If everything is hidden, still show a reasonable domain around 100
      const yMin = allValues.length > 0 ? d3.min(allValues) : 98;
      const yMax = allValues.length > 0 ? d3.max(allValues) : 102;
      const yPadding = Math.max((yMax - yMin) * 0.15, 0.5);

      const y = d3.scaleLinear()
        .domain([yMin - yPadding, yMax + yPadding])
        .nice()
        .range([innerH, 0]);

      // Grid lines
      const yTicks = y.ticks(6);
      g.selectAll('.grid-line')
        .data(yTicks)
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', (d) => y(d))
        .attr('y2', (d) => y(d))
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);

      // Baseline reference line at y=100 (dashed)
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', y(100))
        .attr('y2', y(100))
        .attr('stroke', '#9ca3af')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '6,4');

      // Baseline label
      g.append('text')
        .attr('x', -8)
        .attr('y', y(100))
        .attr('dominant-baseline', 'central')
        .attr('text-anchor', 'end')
        .attr('font-size', '9px')
        .attr('fill', '#9ca3af')
        .text('100');

      // Line generator
      const lineGen = d3.line()
        .x((d, i) => x(parsedDates[i]))
        .y((d) => y(d))
        .defined((d) => d != null)
        .curve(d3.curveMonotoneX);

      // Draw series lines
      activeKeys.forEach((key) => {
        const seriesData = data.series[key];
        if (!seriesData) return;

        const color = data.colors && data.colors[key] ? data.colors[key] : '#6b7280';

        g.append('path')
          .datum(seriesData)
          .attr('d', lineGen)
          .attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-width', 2);

        // End-of-line label: find the last non-null value
        let lastIdx = -1;
        for (let i = seriesData.length - 1; i >= 0; i--) {
          if (seriesData[i] != null) {
            lastIdx = i;
            break;
          }
        }
        if (lastIdx >= 0) {
          const lastVal = seriesData[lastIdx];
          const label = data.labels && data.labels[key] ? data.labels[key] : key;

          g.append('text')
            .attr('x', x(parsedDates[lastIdx]) + 6)
            .attr('y', y(lastVal))
            .attr('dominant-baseline', 'central')
            .attr('font-size', '10px')
            .attr('font-weight', 600)
            .attr('fill', color)
            .text(`${lastVal.toFixed(1)}`);
        }
      });

      // X axis
      g.append('g')
        .attr('transform', `translate(0, ${innerH})`)
        .call(
          d3.axisBottom(x)
            .ticks(Math.min(n, 10))
            .tickSize(0)
            .tickFormat((d) => formatDateLabel(d.toISOString()))
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .attr('transform', 'rotate(-30)')
        .attr('text-anchor', 'end')
        .attr('dx', '-0.5em')
        .attr('dy', '0.25em');

      // Y axis
      g.append('g')
        .call(
          d3.axisLeft(y)
            .ticks(6)
            .tickSize(0)
            .tickFormat((v) => v.toFixed(1))
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280');

      // Crosshair line
      const crosshair = g.append('line')
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3')
        .style('display', 'none');

      // Invisible overlay for mouse tracking
      g.append('rect')
        .attr('width', innerW)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .on('mousemove', (event) => {
          const [mx] = d3.pointer(event);
          const bisect = d3.bisector((d) => d).left;
          const dateAtMouse = x.invert(mx);
          const timestamps = parsedDates.map((d) => d.getTime());
          let idx = bisect(timestamps, dateAtMouse.getTime());
          if (idx > 0 && idx < n) {
            const d0 = timestamps[idx - 1];
            const d1 = timestamps[idx];
            idx = (dateAtMouse.getTime() - d0) > (d1 - dateAtMouse.getTime()) ? idx : idx - 1;
          }
          idx = Math.max(0, Math.min(n - 1, idx));

          const cx = x(parsedDates[idx]);
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          // Build tooltip values sorted by index value descending
          const seriesValues = SERIES_KEYS
            .filter((key) => !hiddenSeries.has(key) && data.series[key])
            .map((key) => ({
              key,
              label: data.labels && data.labels[key] ? data.labels[key] : key,
              value: data.series[key][idx],
              color: data.colors && data.colors[key] ? data.colors[key] : '#6b7280',
            }))
            .filter((sv) => sv.value != null)
            .sort((a, b) => b.value - a.value);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: data.dates[idx],
            seriesValues,
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    return () => {
      cancelAnimationFrame(rafId);
      d3.select(svgRef.current).selectAll('*').remove();
    };
  }, [data, hiddenSeries, resizeCount]);

  // Handle window resize — trigger redraw by bumping a counter
  useEffect(() => {
    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setResizeCount((c) => c + 1);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0 || !data.series) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Service Price Indices</div>
          <div style={styles.subtitle}>
            All service types rebased to 100, showing relative price movement
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No price index data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Service Price Indices</div>
        <div style={styles.subtitle}>
          All service types rebased to 100, showing relative price movement
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {SERIES_KEYS.map((key) => {
          if (!data.series[key]) return null;
          const isHidden = hiddenSeries.has(key);
          const color = data.colors && data.colors[key] ? data.colors[key] : '#6b7280';
          const label = data.labels && data.labels[key] ? data.labels[key] : key;
          return (
            <div
              key={key}
              style={styles.legendItem}
              onClick={() => toggleSeries(key)}
            >
              <svg width="14" height="10">
                <line
                  x1="0"
                  y1="5"
                  x2="14"
                  y2="5"
                  stroke={isHidden ? '#d1d5db' : color}
                  strokeWidth="2"
                />
              </svg>
              <span
                style={{
                  color: isHidden ? '#d1d5db' : '#6b7280',
                  textDecoration: isHidden ? 'line-through' : 'none',
                }}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltip.seriesValues.map((sv) => (
            <div
              key={sv.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: sv.color, fontWeight: 600 }}>{sv.label}</span>
              <span
                style={{
                  fontWeight: 600,
                  color: sv.value >= 100 ? '#16a34a' : '#dc2626',
                }}
              >
                {sv.value.toFixed(1)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
